import { action, computed, observable, when, makeObservable } from "mobx";
import {
  createTransformer,
  fromPromise,
  PENDING,
  type IPromiseBasedObservable,
} from "mobx-utils";

import Api from "nvent-web/services/Api";
import { createInitialFromPromise } from "nvent-web/utils/createInitialFromPromise";
import { type Promotion } from "nvent-web-admin/types/Promotion";
import { UserStore } from "./User";

export class PromotionsStore {
  promotionsPromise = createInitialFromPromise<Promotion[]>([]);

  constructor(
    private user: UserStore,
    private api: Api
  ) {
    makeObservable(this, {
      promotionsPromise: observable.ref,
      arePromotionsLoading: computed,
      promotions: computed,
      promotionsForCurrentUser: computed,
      promotionsByCountryCode: computed,
      loadPromotions: action.bound,
      loadPromotionsForCountry: action.bound,
      setPromotionPromise: action.bound,
    });

    when(
      () => user.isReady,
      async () => {
        await this.loadPromotions();
      }
    );
  }

  get arePromotionsLoading(): boolean {
    return this.promotionsPromise.state === PENDING;
  }

  get promotions(): Promotion[] {
    return (
      this.promotionsPromise.case({
        fulfilled: (promotions) => promotions,
      }) || []
    );
  }

  get promotionsForCurrentUser(): Promotion[] {
    return this.promotionsByCountryCode(
      this.user.companyAddress.countryCode
    ).filter(({ availability, status }) => {
      if (status === "inactive") {
        return false;
      }

      if (availability === "both") {
        return true;
      }

      return this.user.certifiedProNumber
        ? availability === "only_certified_pro"
        : availability === "only_non_certified_pro";
    });
  }

  get promotionsByCountryCode() {
    return createTransformer((countryCode: string) =>
      this.promotions.filter(
        (promotion) =>
          promotion.countryCode.toLowerCase() === countryCode.toLowerCase()
      )
    );
  }

  setPromotionPromise(value: IPromiseBasedObservable<Promotion[]>) {
    this.promotionsPromise = value;
  }

  async loadPromotions() {
    try {
      const response = await this.api.promotions.getAll();
      this.setPromotionPromise(fromPromise(Promise.resolve(response.data)));
      return this.promotionsPromise;
    } catch (error) {
      console.error(error);
    }
  }

  async loadPromotionsForCountry(countryCode: string) {
    try {
      const { data } = await this.api.promotions.getAll(countryCode);
      return data;
    } catch (error) {
      console.error(error);
    }
  }
}
